import { BaseApiService } from '@/services/base-service';
import {
  ApiPagination, BaseResponse, Filters, FiltersPagination, Response,
} from '@/types';
import { ProjectStatuses } from '@/enums';
import { del_mask } from '@/utils';
import { Projects, ProjectToCartRequest, CheckingCartResponse } from '../types';

interface ProjectsList extends BaseResponse, ApiPagination {
  data: {
    items: Projects.Project[],
    partner_contact_persons: string[],
    organizations: string[],
  }
}
interface Regions extends BaseResponse, ApiPagination {
  data: {
    items: Projects.Regions[],
  }
}
interface ProjectsProductsList extends BaseResponse, ApiPagination {
  data: {
    items: Projects.Product[],
  }
}
interface ProjectsEquipmentsList extends BaseResponse, ApiPagination {
  data: {
    items: Projects.Equipments[],
  }
}
interface ProjectsCompetitorsList extends BaseResponse, ApiPagination {
  data: {
    items: Projects.Competitors[],
  }
}
interface ProjectsHistoryList extends BaseResponse, ApiPagination {
  data: {
    items: Projects.History[],
  }
}
interface OrdersRealizationsList extends BaseResponse, ApiPagination {
  data: {
    items: Projects.OrdersRealizations[],
  }
}

export class ProjectsService extends BaseApiService {
  private readonly base_path = 'projects';

  async get_projects(filters: FiltersPagination<Filters>): Promise<ProjectsList> {
    const payload = filters;

    const { data } = await this.http_client.get<ProjectsList>(this.base_path, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async get_project_by_id(id: string | number): Promise<Response<Projects.Project>> {
    const url = `${this.base_path}/${id}`;
    const { data } = await this.http_client.get<Response<Projects.Project>>(url);

    return data;
  }

  async get_products_by_id(filters: FiltersPagination<Filters>, id: string | number): Promise<ProjectsProductsList> {
    const url = `${this.base_path}/${id}/products`;
    const payload = filters;

    const { data } = await this.http_client.get<ProjectsProductsList>(url, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async get_equipments_by_id(filters: FiltersPagination<Filters>, id: string | number): Promise<ProjectsEquipmentsList> {
    const url = `${this.base_path}/${id}/equipments`;
    const payload = filters;

    const { data } = await this.http_client.get<ProjectsEquipmentsList>(url, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async get_competitors_by_id(filters: FiltersPagination<Filters>, id: string | number): Promise<ProjectsCompetitorsList> {
    const url = `${this.base_path}/${id}/competitors`;
    const payload = filters;

    const { data } = await this.http_client.get<ProjectsCompetitorsList>(url, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async get_history_by_id(filters: FiltersPagination<Filters>, id: string | number): Promise<ProjectsHistoryList> {
    const url = `${this.base_path}/${id}/history`;
    const payload = filters;

    const { data } = await this.http_client.get<ProjectsHistoryList>(url, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async get_orders_realizations_by_id(filters: FiltersPagination<Filters>, id: string | number): Promise<OrdersRealizationsList> {
    const url = `${this.base_path}/${id}/orders-realizations`;
    const payload = filters;

    const { data } = await this.http_client.get<OrdersRealizationsList>(url, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async add_new_project(project: Partial<Projects.NewProject>): Promise<number | undefined> {
    const url = `${this.base_path}`;
    const payload = project;

    payload.partner_contact_telephone = del_mask(payload.partner_contact_telephone);
    payload.client_telephone = del_mask(payload.client_telephone);
    payload.client_contact_telephone = del_mask(payload.client_contact_telephone);

    const { data } = await this.http_client.post<Response<null>>(url, payload);

    return data.code;
  }

  async get_regions(): Promise<Regions> {
    const url = `${this.base_path}/regions`;

    const { data } = await this.http_client.get<Regions>(url);

    return data;
  }

  async checking_cart(): Promise<CheckingCartResponse> {
    const url = `${this.base_path}/checking-cart`;

    const { data } = await this.http_client.get<CheckingCartResponse>(url);

    return data;
  }

  async project_to_cart(payload: ProjectToCartRequest): Promise<void> {
    const url = `${this.base_path}/project-to-cart`;

    await this.http_client.post<void>(url, payload);
  }

  async get_catalog_products(payload: Projects.CatalogProductRequest): Promise<Projects.CatalogProductResponse> {
    const payload_filters = payload.filters;

    payload_filters.search = payload_filters.search ? payload_filters.search : '';

    const { data } = await this.http_client.get<Projects.CatalogProductResponse>(`${this.base_path}/catalog_products`, {
      params: {
        filters: payload_filters,
        filters_exist: payload.filters_exist,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }
}

export const default_project = (): Projects.Project => ({
  closed_at: '',
  created_at: '',
  description: '',
  organization: '',
  partner_contact_email: '',
  partner_contact_person: '',
  partner_contact_telephone: '',
  sale_points_quantity: 0,
  shipment_at: '',
  subpartner: '',
  external_number: 0,
  name: '',
  client_name: '',
  products_quantity: 0,
  status: {
    name: '',
    slug: ProjectStatuses.CREATED,
  },
  client: [],
  total_amount: '',
  client_city: '',
  client_contact_email: '',
  client_contact_person: '',
  client_contact_telephone: '',
  client_telephone: '',
  client_website: '',
  action: '',
  project_region_id: null,
});

export const default_new_project = (): Projects.NewProject | Projects.NewProjectClients => ({
  organization_id: null,
  subpartner_id: null,
  shipment_at: '',
  partner_contact_person: '',
  partner_contact_email: '',
  partner_contact_telephone: '',
  sale_points_quantity: null,
  client_name: '',
  client_city: '',
  client_telephone: '',
  client_email: '',
  client_website: '',
  client_contact_person: '',
  client_contact_telephone: '',
  client_contact_email: '',
  description: '',
  client_ids: null,
  project_region_id: null,
});

export const default_catalog_products_filters = (): Projects.CatalogProductRequestFilters => ({
  search: '',
  categories: [],
  software_type: null,
  equipment_models: [],
  is_favorite_only: false,
});
